import { FC, useEffect, useState } from 'react';
import { Atomic, Product } from '@atomicfi/transact-javascript';
import { GetApplicationData } from 'handlers/applicationData';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { createAtomicAccessToken } from 'thunks';
import { RootState } from 'handlers';
import { useSelector } from 'react-redux';

interface AtomicLoginProps {
  open?: boolean;
  onClose: () => void;
  onSuccess: () => void;
  application: GetApplicationData;
  onUpdatePayrollProviderToManual: () => void;
}

enum AtomicDistributionType {
  Fixed = 'fixed',
  Total = 'total',
  Percent = 'percent',
}

interface AtomicDistribution {
  type: AtomicDistributionType;
  amount?: number;
  canUpdate?: boolean;
}

enum AtomicInteraction {
  Failed = 'failed',
  Completed = 'completed',
}

interface AtomicInteractionPayload {
  identifier: string;
  name: string;
  value: {
    state?: AtomicInteraction;
  };
}

const AtomicWidget: FC<AtomicLoginProps> = ({
  open,
  application,
  onClose,
  onSuccess,
  onUpdatePayrollProviderToManual,
}) => {
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const [atomicPublicToken, setAtomicPublicToken] = useState<string | null>(null);
  const [atomicInteractionError, setAtomicInteractionError] = useState<boolean>(false);
  const [atomicWasClosed, setAtomicWasClosed] = useState<boolean>(false);

  const { employerData } = useSelector((state: RootState) => state.employers).getEmployerData;

  useEffect(() => {
    (async () => {
      const publicToken = await dispatchWithUnwrap(createAtomicAccessToken({ applicationId: application.id }));
      setAtomicPublicToken(publicToken);
    })();
  }, []);

  useEffect(() => {
    if (open && atomicPublicToken) {
      openAtomicLogin();
    }
  }, [open]);

  useEffect(() => {
    if (!open && !atomicInteractionError && atomicWasClosed) setAtomicWasClosed(false);
  }, [open, atomicInteractionError, atomicWasClosed]);

  useEffect(() => {
    if (atomicWasClosed && atomicInteractionError) onUpdatePayrollProviderToManual();
  }, [atomicWasClosed, atomicInteractionError]);

  const openAtomicLogin = () => {
    let distribution: AtomicDistribution = {
      type: AtomicDistributionType.Fixed,
      amount: application?.amountPerPaycheck,
    };

    if (application?.fullDepositSwitch) {
      distribution = { type: AtomicDistributionType.Total };
    }

    Atomic.transact({
      config: {
        publicToken: atomicPublicToken,
        tasks: [{ product: Product.DEPOSIT, distribution: { ...distribution } }],
        theme: {
          brandColor: '#795af7',
        },
        ...(employerData && {
          deeplink: {
            step: 'login-company',
            companyId: employerData.id,
          },
        }),
      },
      onDataRequest: (dataRequest: any) => {
        analytics.track(`Atomic: Data Request`, dataRequest);
      },
      onInteraction: (interaction: AtomicInteractionPayload) => {
        if (interaction.value?.state === AtomicInteraction.Failed && !atomicInteractionError) {
          setAtomicInteractionError(true);
        }

        analytics.track(`Atomic: ${interaction.name}`, { value: interaction.value });
      },
      onFinish: () => {
        analytics.track('Atomic: Flow Finished Successfully');
        onSuccess();
      },
      onClose: (closePayload: any) => {
        if (!atomicWasClosed) setAtomicWasClosed(true);

        analytics.track('Atomic: Modal Closed By User', closePayload);
        onClose();
      },
    });
  };

  return null;
};

export default AtomicWidget;
